// REFERENCE-LINES: the metric page's reference lines, and the analysis
// scatter's recommendation limit — no longer safe to delete with the former.

import { Chart, ChartType, Plugin } from 'chart.js'

import {
  ARROW_H,
  ARROW_W,
  getFont,
  getPillText,
  PADDING_X,
  PILL_H,
  RADIUS,
  ReferenceLine
} from './referenceLinePill'

export type DrawHorizontalLinesLine = ReferenceLine

export type DrawHorizontalLinesOpts = {
  lines: DrawHorizontalLinesLine[]
  decimals?: number
  // chart edge the pills sit at; left by default
  labelPosition?: 'left' | 'right'
  significantFigures?: number
}

// a 1px line centred on a pixel row instead of between two, so it is sharp
const snapToPixel = (value: number, devicePixelRatio: number) => {
  const offset = (devicePixelRatio % 2) / 2

  return (
    (Math.round(value * devicePixelRatio - offset) + offset) / devicePixelRatio
  )
}

const getVisibleLines = (
  chart: Chart,
  lines: DrawHorizontalLinesLine[] | undefined
) => {
  const { chartArea, scales } = chart

  if (!scales.y) return []

  return (lines ?? []).flatMap((line) => {
    const yPixel = scales.y.getPixelForValue(line.value)

    return yPixel < chartArea.top || yPixel > chartArea.bottom
      ? []
      : [{ line, yPixel }]
  })
}

/**
 * Chart.js plugin: draws zero or more horizontal reference lines across the
 * chart area, behind the datasets, each with a pill inside the chart at one
 * edge that contains the line's label or value.
 */
export const drawHorizontalLines: Plugin<ChartType, DrawHorizontalLinesOpts> = {
  id: 'drawHorizontalLines',
  beforeDatasetsDraw(chart, _args, opts) {
    const { ctx, chartArea } = chart

    getVisibleLines(chart, opts?.lines).forEach(({ line, yPixel }) => {
      const y = snapToPixel(yPixel, chart.currentDevicePixelRatio)

      // dashed line across the chart area
      ctx.save()
      ctx.strokeStyle = line.color
      ctx.lineWidth = 1
      ctx.setLineDash([5, 3])
      ctx.beginPath()
      ctx.moveTo(chartArea.left, y)
      ctx.lineTo(chartArea.right, y)
      ctx.stroke()
      ctx.restore()
    })
  },
  afterDraw(chart, _args, opts) {
    const { ctx, chartArea } = chart
    const isLeft = (opts?.labelPosition ?? 'left') === 'left'
    // +1 when the pill grows rightwards from the left edge, -1 from the right
    const direction = isLeft ? 1 : -1

    getVisibleLines(chart, opts?.lines).forEach(({ line, yPixel }) => {
      ctx.save()
      ctx.beginPath()
      ctx.rect(0, 0, chart.width, chart.height)
      ctx.clip()

      ctx.font = getFont()

      const text = getPillText(line, opts.decimals, opts.significantFigures)
      const pillW = ctx.measureText(text).width + PADDING_X * 2
      const yClamped = Math.max(
        chartArea.top + PILL_H / 2,
        Math.min(chartArea.bottom - PILL_H / 2, yPixel)
      )

      const edge = isLeft ? chartArea.left : chartArea.right
      const top = yClamped - PILL_H / 2
      const bottom = yClamped + PILL_H / 2
      // arrow tip on the edge, pill growing into the chart
      const arrowBase = edge + direction * ARROW_W
      const far = arrowBase + direction * pillW

      ctx.beginPath()
      ctx.moveTo(edge, yPixel) // arrow tip
      ctx.lineTo(arrowBase, yClamped - ARROW_H / 2)
      ctx.lineTo(arrowBase, top)
      ctx.lineTo(far - direction * RADIUS, top)
      ctx.quadraticCurveTo(far, top, far, top + RADIUS)
      ctx.lineTo(far, bottom - RADIUS)
      ctx.quadraticCurveTo(far, bottom, far - direction * RADIUS, bottom)
      ctx.lineTo(arrowBase, bottom)
      ctx.lineTo(arrowBase, yClamped + ARROW_H / 2)
      ctx.closePath()

      ctx.fillStyle = line.color
      ctx.fill()

      ctx.fillStyle = '#ffffff'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText(text, arrowBase + (direction * pillW) / 2, yClamped)

      ctx.restore()
    })
  }
}
